import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import {
  StartControllerScope,
  useController,
  ApplicationView,
} from '@aha-app/mvc';
import WhiteboardContainerController, {
  type WhiteboardContainerControllerProps,
} from './WhiteboardContainerController';
import Whiteboard from '@aha-app/whiteboard';
import classes from './styles.module.css';
import classnames from 'classnames';
import useDimensions from 'javascripts/util/useDimensions';
import { lookupRecordInfo } from './lookupRecords';
import PageTemplateModal from 'components/page_templates/PageTemplateModal';
import NewPanelModal from 'components/Dashboards/components/NewPanelModal';
import { Whiteboard as WhiteboardModel } from 'components/framework/models';
import { randomId } from '../../javascripts/random_id';
import { useTour } from 'javascripts/hooks/useTour';
import {
  BEST_PRACTICES_PANEL_KEY,
  BestPracticesSidebarItem,
} from './best_practices_panel/BestPracticesPanel';
import {
  getLocalStoragePreference,
  getIsSharedWebpage,
  waitUntilVisible,
} from 'javascripts/utils';
import { TEMPLATE_LOCAL_STORAGE_KEY } from 'components/page_templates/TemplateController';
import ModalMessage from './ModalMessage';
import {
  type CreateableRecords,
  type RecordId,
  type SidePanelItem,
} from 'javascripts/shared-types';
import CommentsSidePanelItem from 'components/side_panels/comments/CommentsSidePanelItem';
import CommentsSidePanelTitleButtons from './comments/CommentsSidePanelTitleButtons';
import TopControls from './TopControls';
import { Link } from './Router';
import { DndContext } from '@aha-app/react-dnd-backend';
import makeJiraIntegration from './integrations/jira/JiraIntegration';
import AdoIntegration from './integrations/ado/AdoIntegration';
import Debug from 'debug';
import historyDataManager from './HistoryData';
import {
  RecordLinkManager,
  RecordManager,
  RelatedRecordManager,
} from './aha_records';
import { useSmartZoneInfo } from './aha_records/smartZones/smartZoneFormData';

const debug = Debug('whiteboard');

const FULL_SCREEN_HEADER = 40;

// container height for when we want the inline whiteboard to fill the available space dynamically
const computeViewportContainerHeight = (
  top: number,
  viewportBottomGap?: number
) => {
  // Use 15px by default to leave some space between the whiteboard and the bottom of the window
  const bottomGap = Math.max(top, 0) + (viewportBottomGap ?? 15);
  return `calc(100vh - ${bottomGap}px)`;
};

function computeReadOnly(editable: Editable, fullScreen: boolean) {
  switch (editable) {
    case WhiteboardContainerController.EDITING_EDITABLE:
      return false;
    case WhiteboardContainerController.EDITING_READ_ONLY:
      return true;
    case WhiteboardContainerController.EDITING_EDITABLE_IN_FULL_SCREEN:
      return !fullScreen;
    default:
      throw new Error(`Unknown whiteboard container editable ${editable}`);
  }
}

// Wrapper for a whiteboard.
export type WhiteboardContainerProps = WhiteboardContainerControllerProps & {
  initialVersion: any;
  initialData: any;
  templateProject?: { id: RecordId; name: string };
  zendeskIconVisible: boolean;
  canFacilitate: boolean;
  viewportBottomGap?: number;
  dimensions: Dimensions;
  allowHistory: boolean;
  canCreateOnly?: CreateableRecords;
  canPresent?: boolean;
  editorV2?: boolean;
};

const WhiteboardContainer: React.FC<WhiteboardContainerProps> = ({
  whiteboardId,
  initialVersion,
  initialData,
  commentsEnabled,
  editor,
  node,
  editorV2,
  showContainerHeader,
  templateProject = null,
  zendeskIconVisible,
  canFacilitate,
  viewportBottomGap,
  dimensions = 'viewportHeight',
  allowHistory,
  canCreateOnly,
  canPresent,
  containingPageId,
}) => {
  const controller = useController(WhiteboardContainerController);
  const setWhiteboardController = useCallback(
    wbController => controller.actionSetWhiteboardController(wbController),
    [controller]
  );

  // canCreateOnly does not change for a given whiteboard. It comes from a rails
  // partial, and is only used for pdlc whiteboards currently
  const [recordManager] = useState(() =>
    RecordManager(controller.getPageId, canCreateOnly)
  );
  const [recordLinkManager] = useState(() => new RecordLinkManager());
  const [relatedRecordManager] = useState(() => new RelatedRecordManager());

  debug('Whiteboard id:', whiteboardId);

  const { collaborative, isShared } = controller;

  const {
    tokenReady,
    editable,
    allowFullScreen,
    allowUpdate,
    allowAnnotate,
    accountId,
    defaultProject,
    commentsVisible,
    displayMode,
    sharingOptions,
    whiteboard,
    whiteboardReady,
    tokenFailure,
  } = controller.state;

  const inline = displayMode.mode === 'inline';
  const fullScreen = displayMode.mode === 'fullscreen';

  const isEditLocked = controller.isEditLocked();
  const inDocNode = useRef<HTMLDivElement>(null);

  const readOnly = !!(
    !collaborative ||
    computeReadOnly(editable, fullScreen) ||
    isEditLocked
  );

  const [ref, { top }] = useDimensions({
    liveMeasure: true,
    ignoreScroll: true,
  });

  const isEmbedded = !!node && !!editor;
  const isEmbeddedPreview = (isEmbedded && inline) || window.isPrinting;

  const commentsSidebarId = useRef(randomId());

  const showWhiteboard =
    !tokenFailure && (!collaborative || (tokenReady && controller.fayeClient));

  const usedTemplateIds = whiteboard?.usedTemplateIds;

  const projectId = templateProject?.id || defaultProject?.id;
  const sidePanelItems = useMemo(() => {
    const sidePanelItems: SidePanelItem[] = [];

    if (collaborative) {
      if (commentsEnabled) {
        sidePanelItems.push(
          CommentsSidePanelItem({
            annotatableType: 'Whiteboard',
            annotatableId: whiteboardId,
            sidebarId: commentsSidebarId.current,
            titleContent: <CommentsSidePanelTitleButtons />,
            baseUrl: sharingOptions.collaborationBaseURL,
            isShared,
            className:
              'whiteboard-annotations__sidebar js-whiteboard-annotations-sidebar',
          })
        );
      }

      if (usedTemplateIds && usedTemplateIds.length > 0) {
        sidePanelItems.push(
          BestPracticesSidebarItem(
            usedTemplateIds,
            'whiteboard',
            content =>
              controller.whiteboardController.actionPlaceContent(content),
            projectId
          )
        );
      }
    }

    if (controller.assistantSidePanel && allowUpdate && !readOnly) {
      sidePanelItems.push(controller.assistantSidePanel);
    }

    return sidePanelItems;
  }, [
    commentsEnabled,
    whiteboardId,
    usedTemplateIds,
    controller.whiteboardController,
    controller.assistantSidePanel,
    sharingOptions.collaborationBaseURL,
    isShared,
    collaborative,
    projectId,
    readOnly,
    allowUpdate,
  ]);

  const [whiteboardNode] = React.useState(() => {
    const elem = document.createElement('div');
    elem.setAttribute(
      'class',
      'whiteboard-container-portal js-annotation-wrapper'
    );
    elem.setAttribute(
      'data-shared-url',
      sharingOptions?.collaborationBaseURL ?? ''
    );
    return elem;
  });

  useLayoutEffect(() => {
    const { whiteboardController } = controller;
    const { mode, previousMode, focusedAnnotationId, transitioned, animate } =
      displayMode;
    const canvas = whiteboardController?.canvas;

    const parentElement = mode === 'inline' ? inDocNode.current : document.body;
    controller.inDocNode = inDocNode.current;
    if (parentElement && whiteboardNode.parentElement !== parentElement) {
      parentElement.appendChild(whiteboardNode);
    }

    if (mode !== 'inline' && whiteboardController) {
      whiteboardController.actionFocus();
    }

    // when transitioning to fullscreen, pan the canvas so that the content
    // stays in the same place relative to the viewport
    // we do the same when exiting fullscreen if the whiteboard remains editable
    // in other cases, when exiting fullscreen we fit the content
    // We ignore presentation mode, as that is handled separately in PresentationController
    if (
      whiteboardReady &&
      canvas &&
      !transitioned &&
      inDocNode.current &&
      ((mode === 'fullscreen' && previousMode === 'inline') ||
        (mode === 'inline' && previousMode === 'fullscreen'))
    ) {
      if (mode === 'inline' && computeReadOnly(editable, false)) {
        // always fit content when returning to a readonly whiteboard
        const container = inDocNode.current.querySelector(
          '.' + classes.container
        );

        if (container instanceof HTMLElement) {
          void waitUntilVisible(container).then(() => {
            canvas.fitCanvasToContainer('fitContent');
          });
        }
      } else {
        const fullScreenPosition = { x: 0, y: FULL_SCREEN_HEADER };
        const containerPosition = inDocNode.current.getBoundingClientRect();

        const multiplier = mode === 'fullscreen' ? 1 : -1;
        const x = multiplier * (containerPosition.x - fullScreenPosition.x);
        const y = multiplier * (containerPosition.y - fullScreenPosition.y);

        canvas.fitCanvasToContainer({ x, y });

        if (mode === 'fullscreen') {
          if (focusedAnnotationId) {
            whiteboardController.actionFocusAnnotation(focusedAnnotationId);
          } else {
            whiteboardController.actionNavigateOrFit({
              animate,
              zoomInOnly: true,
            });
          }
        }
        controller.state.displayMode.transitioned = true;
      }

      document.body.classList.toggle(
        'whiteboard-full-screen-mode',
        mode !== 'inline' && !controller.whiteboardIsInDrawer
      );
    }
  }, [
    controller,
    editable,
    displayMode,
    whiteboardNode,
    controller.whiteboardController,
    whiteboardReady,
  ]);

  // Unmount the portal when unmounting
  useEffect(() => {
    return () => whiteboardNode.remove();
  }, [whiteboardNode]);

  useLayoutEffect(() => {
    const templateId = getLocalStoragePreference(TEMPLATE_LOCAL_STORAGE_KEY);

    /**
     * Once the whiteboard is loaded, check if there is a templateId in
     * local storage. If there is, open the best practices panel and clear
     * that value.
     */
    if (
      templateId &&
      controller.whiteboardController &&
      showWhiteboard &&
      usedTemplateIds
    ) {
      if (usedTemplateIds.length === 1 && usedTemplateIds[0] === templateId) {
        controller.whiteboardController.actionOpenSidePanel(
          BEST_PRACTICES_PANEL_KEY
        );
      }
    }
  }, [usedTemplateIds, showWhiteboard, controller.whiteboardController]);

  const getTourElement = () =>
    whiteboardNode.querySelector<HTMLElement>(':scope .ai-assistant-button');

  useTour({
    key: 'whiteboard.ai_assistant.tour_seen',
    getTarget: getTourElement,
    enabled: !!window.featureFlags?.awt_390_whiteboard_ai_assistant,
    buildTour: tour => {
      tour.addStep({
        title: 'New AI assistant in <br>whiteboards',
        text: 'Use the AI assistant to summarize your whiteboard, arrange shapes by theme, or add Aha! records.',
        attachTo: {
          element: getTourElement(),
          on: 'right',
        },
        hideStepCount: true,
        buttons: [{ text: 'Got it', action: () => tour.complete() }],
        offset: '15px -5px',
      });
    },
  });

  const [integrations] = useState(() => ({
    jira: makeJiraIntegration(),
    ado: new AdoIntegration(),
  }));

  const fullscreenHeaderMessage = useMemo(() => {
    if (
      containingPageId &&
      whiteboard?.page?.id &&
      containingPageId !== whiteboard.page.id &&
      whiteboard.page.name
    ) {
      return (
        <div className='tw-flex tw-gap-2 tw-items-center'>
          <i className='far fa-circle-info' />
          You are {allowUpdate ? 'editing' : 'viewing'}
          <aha-button kind='link' href={whiteboard.page.path}>
            {whiteboard.page.name}
            <i className='far fa-arrow-up-right-from-square' />
          </aha-button>
        </div>
      );
    }

    return null;
  }, [containingPageId, whiteboard, allowUpdate]);

  const smartZoneInfo = useSmartZoneInfo(projectId);

  const fullscreenButton =
    allowFullScreen && !editorV2 && inline ? (
      <Link
        router={controller.router}
        to={controller.getDisplayModeUrl('fullscreen')}
        data-aha-editor-not-new-window
      >
        <aha-button kind='default' aria-label='Enter fullscreen mode'>
          <i className='fa-solid fa-up-right-and-down-left-from-center' />
        </aha-button>
      </Link>
    ) : null;

  return (
    <div
      className={classnames(classes.containerWrapper, {
        [classes.collaborative]: collaborative,
      })}
      style={
        dimensions === 'viewportHeight'
          ? {
              height: computeViewportContainerHeight(
                top ?? 0,
                viewportBottomGap
              ),
            }
          : {}
      }
      ref={inDocNode}
      onDoubleClick={() => {
        if (
          allowFullScreen &&
          (showContainerHeader || editorV2) &&
          displayMode.mode !== 'fullscreen'
        ) {
          controller.router.navigate(
            controller.getDisplayModeUrl('fullscreen')
          );
        }
      }}
      data-whiteboard-controller-store-element
    >
      {ReactDOM.createPortal(
        <div
          ref={ref}
          className={classnames(classes.container, {
            [classes.containerFullScreen]: !inline,
            'whiteboard-fullscreen': fullScreen,
            [classes.hasHeader]: fullScreen,
          })}
          data-whiteboard-id={whiteboardId}
        >
          {showWhiteboard ? (
            <>
              <TopControls
                node={node}
                editorV2={editorV2}
                editor={editor}
                showContainerHeader={showContainerHeader}
                collaborative={collaborative}
              >
                {fullscreenHeaderMessage}
              </TopControls>
              <Whiteboard
                // we don't properly handle these changing (see LoopbackClient initialisation) so
                // just remount in the unlikely case that they do change
                key={JSON.stringify({ allowUpdate, allowAnnotate })}
                controllerRef={setWhiteboardController}
                readOnly={readOnly}
                allowUpdate={allowUpdate}
                allowAnnotate={allowAnnotate}
                isEmbeddedPreview={isEmbeddedPreview}
                connection={controller.shareDBConnection}
                fayeClient={controller.fayeClient ?? undefined}
                documentCollection={controller.documentCollection()}
                documentId={controller.documentId()}
                whiteboardId={whiteboardId}
                whiteboardUrl={controller.state.whiteboardUrl ?? undefined}
                accountId={accountId as RecordId | undefined}
                userId={window.currentUser?.id as RecordId | undefined}
                initialVersion={initialVersion}
                initialData={initialData}
                shouldShowEmptyState={showContainerHeader && inline}
                zendeskIconVisible={zendeskIconVisible && inline}
                resolveUserInfo={async userId => {
                  const { name, avatar } = await controller.lookupUserInfo(
                    userId
                  );
                  return { displayName: name, avatar: avatar ?? undefined };
                }}
                resolveRecordInfo={
                  controller.fromOtherAccount() || getIsSharedWebpage()
                    ? undefined
                    : lookupRecordInfo
                }
                openAhaDrawer={controller.actionOpenAhaDrawer}
                openRecordInDrawer={controller.actionOpenRecordInDrawer}
                resolveAttachmentInfo={attachmentId =>
                  controller.resolveAttachmentInfo(attachmentId)
                }
                defaultProject={defaultProject}
                uploadAttachment={async ({ file }) =>
                  controller.uploadAttachment({ file })
                }
                duplicateAttachments={async (
                  attachmentMapping,
                  originalWhiteboardId
                ) =>
                  controller.duplicateAttachments(
                    attachmentMapping,
                    originalWhiteboardId
                  )
                }
                renderAddAhaViewModal={callbacks => (
                  <NewPanelModal
                    open
                    maxPanels={100}
                    panelCount={1}
                    closePanelModal={() =>
                      callbacks.actionSetShowAddAhaViewModal(false)
                    }
                    addPanelFromExistingReport={selectedReport => {
                      callbacks.actionPlaceEmbeddedBookmark(selectedReport.id);
                      callbacks.actionSetShowAddAhaViewModal(false);
                    }}
                    addExtensionPanel={false}
                    context='whiteboard'
                    enableCopying={false}
                    defaultView={'existingReports'}
                  />
                )}
                selectAhaRecords={controller.actionSelectAhaRecords}
                renderTemplateModal={callbacks => (
                  <PageTemplateModal
                    templateType='whiteboard'
                    projectId={templateProject?.id || defaultProject?.id}
                    placeTemplate={async (template, isGlobal) => {
                      await callbacks.actionPlaceTemplate(template);

                      if (isGlobal) {
                        await WhiteboardModel.linkToTemplate(
                          whiteboardId,
                          template.id
                        );

                        callbacks.actionOpenSidePanel(BEST_PRACTICES_PANEL_KEY);
                      }
                    }}
                    closeTemplateModal={() =>
                      callbacks.actionAbortChooseTemplate()
                    }
                  />
                )}
                commentsEnabled={!!(commentsEnabled && collaborative)}
                commentsVisible={commentsVisible}
                externalSetCommentsVisible={(visible: boolean) =>
                  controller.actionSetCommentsVisible(visible)
                }
                commentsSidebarId={commentsSidebarId.current}
                sidePanelItems={sidePanelItems}
                canFacilitate={
                  canFacilitate && !computeReadOnly(editable, fullScreen)
                }
                canRequestFilename={controller.canRequestFilename()}
                attachmentUrls={controller.attachmentUrls}
                mode={displayMode.mode}
                setIsPresenting={(presenting: boolean) => {
                  controller.router.navigate(
                    controller.getDisplayModeUrl(
                      presenting
                        ? 'presentation'
                        : displayMode.previousMode ?? 'inline'
                    )
                  );
                }}
                sharingOptions={sharingOptions}
                dragDropManager={DndContext.dragDropManager}
                sidepanelDisabled={controller.props.sidepanelDisabled}
                integrations={integrations}
                openCardLayoutEditor={controller.actionOpenCardLayoutEditor}
                historyDataManager={historyDataManager}
                loadDefaultCardLayout={controller.loadDefaultCardLayout}
                recordManager={recordManager}
                recordLinkManager={recordLinkManager}
                relatedRecordManager={relatedRecordManager}
                allowHistory={allowHistory}
                canPresent={canPresent}
                updateRecords={smartZoneInfo?.updateRecords}
                smartZoneFormData={smartZoneInfo?.formData}
                renderControls={fullscreenButton}
              />
            </>
          ) : (
            // Placeholder until whiteboard is ready
            <div
              style={{
                backgroundColor: 'var(--theme-tertiary-background)',
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {tokenFailure && (
                <p>There was an error loading the whiteboard.</p>
              )}
            </div>
          )}
        </div>,
        whiteboardNode
      )}
      <ModalMessage />
    </div>
  );
};

const ControlledWhiteboardContainer = StartControllerScope(
  WhiteboardContainerController,
  ApplicationView(WhiteboardContainer)
);

export default ControlledWhiteboardContainer;
